import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Skeleton, Badge, Empty, ErrorBox } from "@/components/ui";
import type { Patient } from "@/types";

const PHASES = [
  "", "workup", "rt_decision", "ct_sim_pending",
  "on_treatment", "follow_up", "completed", "inactive",
];
const DIAGNOSES = [
  "", "breast", "prostate", "lung", "rectal_gi", "head_neck",
  "cns", "gyne", "sarcoma", "palliative", "other",
];
const URGENCIES = ["", "routine", "urgent", "critical"];
type Tone = "ok" | "warn" | "bad" | "info" | "muted";

function normalizeTone(value?: string | null): Tone {
  return ["ok", "warn", "bad", "info", "muted"].includes(String(value || ""))
    ? (value as Tone)
    : "muted";
}

function registryRowClass(p: Patient): string {
  if (p.follow_up_update_missing) return "bg-amber-950/25 hover:bg-amber-900/30 border-l-4 border-amber-500/80";
  if (String(p.follow_up_status || "").startsWith("complete")) {
    return "bg-emerald-950/20 hover:bg-emerald-900/25 border-l-4 border-emerald-500/70";
  }
  return "hover:bg-panel2/60";
}

export default function Registry() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [urgency, setUrgency] = useState("");
  const [archiveFilter, setArchiveFilter] = useState<"active" | "archived" | "all">("active");

  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  if (status) qs.set("status", status);
  if (diagnosis) qs.set("diagnosis", diagnosis);
  if (urgency) qs.set("urgency", urgency);
  if (archiveFilter !== "active") qs.set("include_archived", "true");

  const { data, isLoading, error } = useQuery({
    queryKey: ["patients", qs.toString()],
    queryFn: () => api.get<Patient[]>(`/patients?${qs.toString()}`),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-50">Patient registry</h1>
        <div className="text-sm text-slate-400">
          Search, filter, and open patient files.
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="label">Search</label>
            <input
              className="input"
              placeholder="Name, MRN, diagnosis…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Treatment phase</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              {PHASES.map((p) => (
                <option key={p} value={p}>{p || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Diagnosis group</label>
            <select className="input" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)}>
              {DIAGNOSES.map((d) => (
                <option key={d} value={d}>{d || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Urgency</label>
            <select className="input" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
              {URGENCIES.map((u) => (
                <option key={u} value={u}>{u || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Archive filter</label>
            <select className="input" value={archiveFilter} onChange={(e) => setArchiveFilter(e.target.value as any)}>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
      </Card>

      {error ? <ErrorBox error={error} /> : null}
      {isLoading ? (
        <Card><Skeleton rows={6} /></Card>
      ) : !data || data.length === 0 ? (
        <Empty title="No patients match." hint="Adjust filters or seed demo data." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>MRN</th>
                  <th>Patient</th>
                  <th>Diagnosis</th>
                  <th>Phase</th>
                  <th>Urgency</th>
                  <th>Status / Next action</th>
                </tr>
              </thead>
              <tbody>
                {data.filter((p) => archiveFilter === "all" || (archiveFilter === "archived" ? p.is_archived : !p.is_archived)).map((p) => (
                  <tr key={p.id} className={registryRowClass(p)}>
                    <td className="font-mono text-xs text-slate-400">{p.mrn}</td>
                    <td>
                      <Link className="text-accent hover:underline" to={`/patients/${p.id}`}>
                        {p.full_name}
                      </Link>
                      {p.is_archived ? <Badge tone="muted">archived</Badge> : null}
                      <div className="text-xs text-slate-500">
                        {p.sex} · DOB {p.date_of_birth || "—"}
                      </div>
                    </td>
                    <td>
                      <div className="text-slate-200">{p.primary_diagnosis || "—"}</div>
                      <div className="text-xs text-slate-500">
                        {p.stage_summary || ""}
                        {p.tnm_t || p.tnm_n || p.tnm_m
                          ? ` · ${p.tnm_t || ""}${p.tnm_n || ""}${p.tnm_m || ""}`
                          : ""}
                      </div>
                    </td>
                    <td><Badge tone="info">{p.treatment_phase}</Badge></td>
                    <td>
                      <Badge tone={p.urgency === "critical" ? "bad" : p.urgency === "urgent" ? "warn" : "muted"}>
                        {p.urgency}
                      </Badge>
                    </td>
                    <td className="max-w-md">
                      <div className="flex flex-wrap items-center gap-2">
                        {p.follow_up_status_label ? (
                          <Badge tone={normalizeTone(p.follow_up_status_tone)}>{p.follow_up_status_label}</Badge>
                        ) : null}
                        <span className="text-slate-200 truncate">{p.status_label || "—"}</span>
                      </div>
                      <div className={p.follow_up_update_missing ? "text-xs text-amber-200 truncate" : "text-xs text-slate-500 truncate"}>
                        {p.follow_up_reminder || p.next_action_text || "No next action recorded"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
